import React from 'react';
import type { ProviderDetail } from '../../../../../js/api';
import { CopyableText } from '../../../components/copyable-text';
import { EmailContact } from '../../../components/email-contact';
import { TooltippedText } from '../../../components/tooltipped-text';
import { AvailabilityUrlCopy } from '../../Provider/ProviderHome/AvailabilityUrlCopy';
import { EMPTY_VALUE, formatProviderDate, labelsFor, ProviderDrawerMaps } from './providerDisplay';

const ICON = 'fa-solid fa-fw tw:text-brand-green-0 tw:shrink-0 tw:mr-2';

/** Green at-a-glance strip under the drawer header: identity + contact + specialties. */
export function ProviderQuickDetails({
  provider,
  maps,
}: {
  provider: ProviderDetail;
  maps: ProviderDrawerMaps;
}) {
  const items: Array<{ key: string; icon: string; tooltip: string; value: React.ReactNode }> = [
    {
      key: 'dob',
      icon: 'fa-calendar',
      tooltip: 'Date of birth',
      value: formatProviderDate(provider.birth_date),
    },
    {
      key: 'email',
      icon: 'fa-envelope',
      tooltip: 'Email',
      value: provider.email ? <EmailContact email={provider.email} /> : EMPTY_VALUE,
    },
    {
      key: 'phone',
      icon: 'fa-phone',
      tooltip: 'Phone',
      value: provider.phone_number ? <CopyableText value={provider.phone_number} /> : EMPTY_VALUE,
    },
    {
      key: 'texting',
      icon: 'fa-comment-sms',
      tooltip: 'Texting number',
      value: provider.texting_number ? (
        <CopyableText value={provider.texting_number} />
      ) : (
        'Opted out'
      ),
    },
  ];

  return (
    <div
      className="tw:bg-brand-green-0/10 tw:rounded-lg tw:p-3 tw:text-zinc-700 tw:leading-tight"
      data-testid="provider-quick-details"
    >
      {/* Two equal-height columns: the right one is justified top/bottom so NPI sits
          beside DOB and Accepting Connections beside Texting, with specialties in
          between and free to grow without shifting the left column. */}
      <div className="tw:grid tw:grid-cols-1 tw:@xl:grid-cols-2 tw:gap-x-3 tw:gap-y-3 tw:items-stretch">
        <div className="tw:flex tw:flex-col tw:gap-2 tw:min-w-0">
          {items.map((item) => (
            <div key={item.key} className="tw:flex tw:items-center tw:gap-2 tw:min-w-0">
              <TooltippedText tooltipContent={item.tooltip}>
                <i className={`${ICON} ${item.icon}`} />
                <span className="tw:break-words">{item.value}</span>
              </TooltippedText>
            </div>
          ))}
        </div>
        <div className="tw:flex tw:flex-col tw:justify-between tw:gap-2 tw:min-w-0">
          <div className="tw:flex tw:items-center tw:gap-2 tw:min-w-0">
            <TooltippedText tooltipContent="National Provider Identifier">
              <i className={`${ICON} fa-id-card`} />
              {provider.npi ? <CopyableText value={provider.npi} /> : <span>{EMPTY_VALUE}</span>}
            </TooltippedText>
          </div>
          <div className="tw:flex tw:flex-1 tw:items-start tw:min-w-0">
            <TooltippedText tooltipContent="Specialties">
              <i className={`${ICON} fa-stethoscope tw:mt-0.5`} />
            </TooltippedText>
            <ul className="tw:flex tw:flex-col tw:gap-1 tw:list-none tw:!m-0 tw:!p-0 tw:min-w-0">
              {(provider.specialties?.length ? provider.specialties : [null]).map((code) => (
                <li key={code ?? 'none'} className="tw:break-words">
                  {code ? labelsFor([code], maps.Specialty) : EMPTY_VALUE}
                </li>
              ))}
            </ul>
          </div>
          {/* The icon is the value: check = accepting, slashed circle = not. */}
          <div
            className="tw:flex tw:items-center tw:min-w-0"
            data-testid="provider-accepting-connections"
            data-accepting={provider.accepting_connections ? 'true' : 'false'}
          >
            <i
              className={
                provider.accepting_connections
                  ? 'fa-solid fa-fw fa-circle-check tw:text-brand-green-0 tw:shrink-0 tw:mr-2'
                  : 'fa-solid fa-fw fa-ban tw:text-stoplight-danger tw:shrink-0 tw:mr-2'
              }
              aria-label={
                provider.accepting_connections
                  ? 'Accepting connections'
                  : 'Not accepting connections'
              }
            />
            <span className="tw:break-words">Accepting Connections</span>
          </div>
          {/* Copy-to-clipboard scheduling link (same control as the provider home). */}
          {provider.availability_url && (
            <div
              className="tw:flex tw:items-center tw:min-w-0"
              data-testid="provider-availability-link"
            >
              <i className={`${ICON} fa-calendar-days`} />
              <AvailabilityUrlCopy
                availabilityUrl={provider.availability_url}
                label="Share Schedule Link"
                className="tw:justify-start tw:mb-0"
                btnClassName="tw:!p-0 tw:!border-0 tw:!leading-tight tw:!align-baseline tw:!text-brand-green-0 tw:hover:!text-brand-green-1"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
